import { LLM_MODEL_PRICING } from './llm-pricing';

/**
 * The Claude model the agent LLM provider runs when nothing names one, or when
 * the named model is retired or is not a Claude model (PD-16962). It is the
 * Claude provider's own fallback and the factory's Claude default, kept in one
 * place so they cannot drift apart again (the id used to be repeated in four
 * places). It does not choose the platform's engine: that is `PLATFORM_LLM`.
 */
export const DEFAULT_CLAUDE_MODEL = 'claude-sonnet-5';

/**
 * The LLM models the platform supports, in one place (PD-16759).
 *
 * The provider cost table (`LLM_MODEL_COSTS`) must cover this list: a
 * supported model with no cost entry records a NULL cost, so
 * `llm-pricing.test.ts` checks every id here.
 *
 * `AGENT_LLM_MODELS` is the list crm-api's `LLMFactoryService.getProviderInfo`
 * declares for the conversation agents; it moved here unchanged so the
 * cost coverage test sees the same list the factory serves.
 *
 * PD-16962: the Claude list holds only models the API still serves. The
 * Claude 3 family and Claude Sonnet 4 / Opus 4 (`-20250514`) are retired and
 * answer 404, so they are not offered; the provider maps a stored id of a
 * retired model to `DEFAULT_CLAUDE_MODEL` instead of failing the request.
 * Their cost entries stay in `LLM_MODEL_COSTS` so historical costs remain
 * recomputable.
 *
 * PD-16964: this is also the list Workd associates choose an agent's chat and
 * voice model from (constants/agent-llm.ts). An agent whose stored model is
 * removed from it falls back (voice to chat, chat to the platform default)
 * and its conversations log a warning until another model is chosen.
 */
export const AGENT_LLM_MODELS: Readonly<{
	claude: ReadonlyArray<string>;
	openai: ReadonlyArray<string>;
}> = {
	claude: [
		'claude-haiku-5-5',
		'claude-sonnet-5-5',
		'claude-opus-5-5',
		'claude-sonnet-5',
		'claude-opus-4-8',
		'claude-haiku-4-5-20251001',
	],
	// gpt-6-luna (PD-14427): an evaluation candidate; it takes tools only at
	// reasoning effort 'none' on Chat Completions (openai-model-capability).
	openai: ['gpt-5.4', 'gpt-5.4-mini', 'gpt-5.4-nano', 'gpt-6-luna'],
};

/**
 * Every model id the platform may call: the agent models plus the models
 * the background pipelines may be configured to run. May contain a dated
 * snapshot and its alias side by side (`claude-haiku-4-5-20251001` and
 * `claude-haiku-4-5`).
 */
export const SUPPORTED_LLM_MODEL_IDS: ReadonlyArray<string> = Array.from(
	new Set([
		...AGENT_LLM_MODELS.claude,
		...AGENT_LLM_MODELS.openai,
		...LLM_MODEL_PRICING.map((entry) => entry.modelId),
	]),
);
